/*

  In order to create new icons:

  1. Create the icon svg:
      -> the svg should be outlined (https://iconly.io/tools/svg-convert-stroke-to-fill)
      -> svg width and height properties set to "100%"
      -> svg fill set to "inherit"
      -> fills from path elements removed
  2. Add a ComponentNameIcon.tsx file wrapping the icon svg to assets/icons folder
  3. Import the component here
  4. Add the ComponentNameIcon to iconComponents map
  5. Add the component-name to iconList array

*/

import classNames from 'classnames'
import React, {Suspense} from 'react'

import AddDataIcon from '../../assets/icons/AddDataIcon'
import AddDocumentIcon from '../../assets/icons/AddDocumentIcon'
import AddInvoiceIcon from '../../assets/icons/AddInvoiceIcon'
import ArrowDownIcon from '../../assets/icons/ArrowDownIcon'
import ArrowLeftIcon from '../../assets/icons/ArrowLeftIcon'
import ArrowRightIcon from '../../assets/icons/ArrowRightIcon'
import ArrowRightOnRectIcon from '../../assets/icons/ArrowRightOnRectIcon'
import ArrowUpIcon from '../../assets/icons/ArrowUpIcon'
import AttentionIcon from '../../assets/icons/AttentionIcon'
import BellIcon from '../../assets/icons/BellIcon'
import BoltIcon from '../../assets/icons/BoltIcon'
import BookmarkIcon from '../../assets/icons/BookmarkIcon'
import BusIcon from '../../assets/icons/BusIcon'
import BusinessIcon from '../../assets/icons/BusinessIcon'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import CarbonIcon from '../../assets/icons/CarbonIcon'
import CarIcon from '../../assets/icons/CarIcon'
import CheckmarkCircleIcon from '../../assets/icons/CheckmarkCircleIcon'
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon'
import ChevronLeftIcon from '../../assets/icons/ChevronLeftIcon'
import ChevronRightIcon from '../../assets/icons/ChevronRightIcon'
import ChevronUpIcon from '../../assets/icons/ChevronUpIcon'
import Co2BubbleIcon from '../../assets/icons/Co2BubbleIcon'
import Co2Icon from '../../assets/icons/Co2Icon'
import ColorsIcon from '../../assets/icons/ColorsIcon'
import ColumnsOutlineIcon from '../../assets/icons/ColumnsOutlineIcon'
import ComponentIcon from '../../assets/icons/ComponentIcon'
import CrossIcon from '../../assets/icons/CrossIcon'
import DashboardIcon from '../../assets/icons/DashboardIcon'
import DateIcon from '../../assets/icons/DateIcon'
import DotsIcon from '../../assets/icons/DotsIcon'
import DoubleLocationIcon from '../../assets/icons/DoubleLocationIcon'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import EcoLeafIcon from '../../assets/icons/EcoLeafIcon'
import EditIcon from '../../assets/icons/EditIcon'
import EmailIcon from '../../assets/icons/EmailIcon'
import EmailInboxIcon from '../../assets/icons/EmailInboxIcon'
import EngineIcon from '../../assets/icons/EngineIcon'
import ErrorIcon from '../../assets/icons/ErrorIcon'
import EyeIcon from '../../assets/icons/EyeIcon'
import EyeSlashIcon from '../../assets/icons/EyeSlashIcon'
import FactoryIcon from '../../assets/icons/FactoryIcon'
import FileTableIcon from '../../assets/icons/FileTableIcon'
import FilterIcon from '../../assets/icons/FilterIcon'
import FolderIcon from '../../assets/icons/FolderIcon'
import FuelIcon from '../../assets/icons/FuelIcon'
import GearIcon from '../../assets/icons/GearIcon'
import HighriseIcon from '../../assets/icons/HighriseIcon'
import HomeIcon from '../../assets/icons/HomeIcon'
import IndustryIcon from '../../assets/icons/IndustryIcon'
import InputIcon from '../../assets/icons/InputIcon'
import InvoiceIcon from '../../assets/icons/InvoiceIcon'
import KeyIcon from '../../assets/icons/KeyIcon'
import LeafIcon from '../../assets/icons/LeafIcon'
import LightbulbIcon from '../../assets/icons/LightbulbIcon'
import LoginIcon from '../../assets/icons/LoginIcon'
import LogoutIcon from '../../assets/icons/LogoutIcon'
import MagnifierIcon from '../../assets/icons/MagnifierIcon'
import MaximizeIcon from '../../assets/icons/MaximizeIcon'
import MenuIcon from '../../assets/icons/MenuIcon'
import MinimizeIcon from '../../assets/icons/MinimizeIcon'
import MoneyIcon from '../../assets/icons/MoneyIcon'
import OpenIcon from '../../assets/icons/OpenIcon'
import OvalIcon from '../../assets/icons/OvalIcon'
import PaperIcon from '../../assets/icons/PaperIcon'
import PdfIcon from '../../assets/icons/PdfIcon'
import PencilIcon from '../../assets/icons/PencilIcon'
import PercentageIcon from '../../assets/icons/PercentageIcon'
import PhoneIcon from '../../assets/icons/PhoneIcon'
import PlasticIcon from '../../assets/icons/PlasticIcon'
import PlusIcon from '../../assets/icons/PlusIcon'
import QuestionIcon from '../../assets/icons/QuestionIcon'
import RecycleBinIcon from '../../assets/icons/RecycleBinIcon'
import RecycleIcon from '../../assets/icons/RecycleIcon'
import RenewIcon from '../../assets/icons/RenewIcon'
import SalesIcon from '../../assets/icons/SalesIcon'
import SpreadsheetIcon from '../../assets/icons/SpreadsheetIcon'
import SuccessIcon from '../../assets/icons/SuccessIcon'
import TableIcon from '../../assets/icons/TableIcon'
import TextIcon from '../../assets/icons/TextIcon'
import TickIcon from '../../assets/icons/TickIcon'
import TruckIcon from '../../assets/icons/TruckIcon'
import UploadIcon from '../../assets/icons/UploadIcon'
import UserIcon from '../../assets/icons/UserIcon'
import UserManyIcon from '../../assets/icons/UserManyIcon'
import VerificationIcon from '../../assets/icons/VerificationIcon'

export type Icons = typeof iconList[number];
interface IconComponent {
  [componentName: string]: () => JSX.Element;
}

export type IconSizes = 'sm' | 'md' | 'lg' | 'full';
export interface IconProps {
  icon: Icons;
  onClick?: () => void;
  className?: string;
  size?: IconSizes;
}

export const iconList = [
  'add-data',
  'add-document',
  'add-invoice',
  'arrow-down',
  'arrow-left',
  'arrow-right',
  'arrow-right-on-rect',
  'arrow-up',
  'attention',
  'bell',
  'bolt',
  'bookmark',
  'bus',
  'business',
  'calendar',
  'car',
  'carbon',
  'checkmark-circle',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'co2',
  'co2-bubble',
  'colors',
  'columns-outline',
  'component',
  'cross',
  'dashboard',
  'date',
  'dots',
  'double-location',
  'download',
  'eco-leaf',
  'edit',
  'email',
  'email-inbox',
  'engine',
  'error',
  'eye',
  'eye-slash',
  'factory',
  'file-table',
  'filter',
  'folder',
  'fuel',
  'gear',
  'highrise',
  'home',
  'industry',
  'input',
  'invoice',
  'key',
  'leaf',
  'lightbulb',
  'login',
  'logout',
  'magnifier',
  'maximize',
  'menu',
  'minimize',
  'money',
  'open',
  'oval',
  'paper',
  'pdf',
  'pencil',
  'percentage',
  'phone',
  'plastic',
  'plus',
  'question',
  'recycle',
  'recycle-bin',
  'renew',
  'sales',
  'spreadsheet',
  'success',
  'table',
  'text',
  'tick',
  'truck',
  'upload',
  'user',
  'user-many',
  'verification'
] as const

const iconComponents: IconComponent = {
  AddDataIcon,
  CarbonIcon,
  PercentageIcon,
  TruckIcon,
  ErrorIcon,
  DownloadIcon,
  AddDocumentIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowRightOnRectIcon,
  AttentionIcon,
  BoltIcon,
  BookmarkIcon,
  CalendarIcon,
  CarIcon,
  Co2BubbleIcon,
  ColorsIcon,
  ColumnsOutlineIcon,
  CrossIcon,
  DotsIcon,
  DoubleLocationIcon,
  EcoLeafIcon,
  EditIcon,
  EyeIcon,
  EyeSlashIcon,
  FileTableIcon,
  FilterIcon,
  FolderIcon,
  GearIcon,
  HighriseIcon,
  HomeIcon,
  IndustryIcon,
  InputIcon,
  InvoiceIcon,
  LightbulbIcon,
  MenuIcon,
  OpenIcon,
  MagnifierIcon,
  MaximizeIcon,
  MinimizeIcon,
  LoginIcon,
  LogoutIcon,
  MoneyIcon,
  OvalIcon,
  PaperIcon,
  PencilIcon,
  PlasticIcon,
  PlusIcon,
  QuestionIcon,
  RecycleBinIcon,
  RecycleIcon,
  SpreadsheetIcon,
  TableIcon,
  TextIcon,
  TickIcon,
  UploadIcon,
  UserIcon,
  UserManyIcon,
  EmailIcon,
  PhoneIcon,
  FactoryIcon,
  ComponentIcon,
  BellIcon,
  VerificationIcon,
  BusinessIcon,
  SalesIcon,
  DashboardIcon,
  EmailInboxIcon,
  LeafIcon,
  EngineIcon,
  FuelIcon,
  CheckmarkCircleIcon,
  PdfIcon,
  RenewIcon,
  SuccessIcon,
  DateIcon,
  KeyIcon,
  BusIcon,
  Co2Icon,
  AddInvoiceIcon
}

const buildIconComponentName = (iconProp: string) => {
  const iconSuffix = 'Icon'
  const replaceRegex = /(^\w|-\w)/g // Target the first character and each dash with the following character

  const iconName = iconProp.replace(replaceRegex, subStr =>
    subStr
      .replace(/-/, '') // remove dashes
      .toUpperCase()
  )

  return iconName + iconSuffix
}

const Icon = ({icon, className, size = 'md', onClick}: IconProps) => {
  const iconComponentName = buildIconComponentName(icon)
  const IconComponent = iconComponents[iconComponentName]

  const iconWrapperClasses = classNames(
    onClick ? 'cursor-pointer' : 'cursor-[inherit]',
    className,
    {
      'w-4 h-4 p-px': size === 'sm',
      'w-6 h-6 p-0.5': size === 'md',
      'w-8 h-8 p-1': size === 'lg',
      'w-full h-full': size === 'full'
    }
  )

  return (
    <div
      className={iconWrapperClasses}
      onClick={onClick}
      tabIndex={onClick ? 0 : -1}
    >
      <Suspense>
        <IconComponent />
      </Suspense>
    </div>
  )
}

export default Icon
